import { modules } from "byzantine";
import { Button } from "@narmi/design_system";
import { Localized, useLocalization } from "@fluent/react";
import { memo, useCallback } from "react";
import styles from "./DashboardCustomizationDrawer.module.scss";
import SubDrawer from "../SubDrawer";
import { useDashboardCustomizationContext } from "./DashboardCustomizationContext";
import SortableMultiColumnList from "../../../src/components/dragAndDrop/SortableMultiColumnList";
import useDashboardCustomizationRows from "./hooks/useDashboardCustomizationRows";
import ResetCustomizationButton from "./components/ResetCustomizationButton";

const { useIsDashboardCustomizationEnabled } = modules.userDisplayPreferences;
const { useDashboardLayoutRedesignEnabled } = modules.dashboard;

const TopMenu = memo(
  ({ onClose, onSave }: { onClose: () => void; onSave: () => void }) => {
    const { l10n } = useLocalization();
    return (
      <div className={styles["top-menu"]}>
        <div className={styles["top-menu-back"]}>
          <span className="narmi-icon-chevron-left" />
          <Button onClick={onClose} kind="plain">
            {l10n.getString("button-back", null)}
          </Button>
        </div>
        <div className={styles["top-menu-title"]}>
          {l10n.getString("dashboard-customization-title", null)}
        </div>
        <Button onClick={onSave}>{l10n.getString("button-save", null)}</Button>
      </div>
    );
  }
);

const DashboardCustomizationDrawer = () => {
  const { isDashboardCustomizationEnabled } =
    useIsDashboardCustomizationEnabled();

  const {
    isCustomizeDialogOpen,
    closeDialogHandler,
    submitCustomizationHandler,
    isPageReloadRequired,
    order,
    setOrder
  } = useDashboardCustomizationContext();
  const { announcements, renderHalfRow, renderRow } =
    useDashboardCustomizationRows();

  const isDashboardLayoutRedesignEnabled = useDashboardLayoutRedesignEnabled();

  const submitHandler = useCallback(() => {
    submitCustomizationHandler(isPageReloadRequired);
  }, [submitCustomizationHandler, isPageReloadRequired]);

  if (!isDashboardLayoutRedesignEnabled || !isDashboardCustomizationEnabled) {
    return null;
  }

  return (
    <SubDrawer
      children={
        <>
          <TopMenu onClose={closeDialogHandler} onSave={submitHandler} />
          <div className={styles["drag-section"]}>
            <Localized id="dashboard-customization-explanation" />
            <SortableMultiColumnList
              items={order}
              onReorder={setOrder}
              renderItem={renderRow}
              renderHalfRow={renderHalfRow}
              announcements={announcements}
            />
            <ResetCustomizationButton />
          </div>
        </>
      }
      isOpen={isCustomizeDialogOpen}
    />
  );
};

export default DashboardCustomizationDrawer;
